// IdpForm.tsx
import { ReactElement, useMemo } from 'react';
import { Control, FormState, useWatch } from 'react-hook-form';
import { IdentityProvider, GetOrganizationResponse, UserRole } from '@localstack/types';
import {
  ControlledCheckbox,
  ControlledDropzone,
  ControlledSelect,
  ControlledTextField,
  CodeVisualizer,
} from '@localstack/ui';
import { MenuItem, Grid, Typography, Box, TextField, Select, Alert } from '@mui/material';

import { PERMISSION_GROUPS, PERMISSION_TYPES, ExternalLink, TEST_IDS } from '@localstack/constants';
import { VALIDATION_RULES } from '@localstack/services';

import { BASE_URL, COGNITO_BASE_URL, COGNITO_IDENTIFIER } from '~/config';

import { FormGridContainer, SectionItemGrid, StrictModeAlertBox, RightHint, InlineLink } from './styled';
import { IdpFormData, MetadataType } from './types';

type Props = {
  control: Control<IdpFormData>;
  formState: FormState<IdpFormData>;
  selectedProvider: Optional<IdentityProvider>;
  organization?: Optional<GetOrganizationResponse>;
  metadataType: MetadataType;
  onMetadataTypeChange: (metadataType: MetadataType) => void;
};

export const IdpForm = ({
  control,
  selectedProvider,
  organization,
  metadataType,
  onMetadataTypeChange,
}: Props): ReactElement => {
  const { required: REQUIRED_RULE, https: HTTPS_RULE } = VALIDATION_RULES;

  // derive from form
  const providerType = useWatch({ control, name: 'idp.provider_type' }) as string | undefined;
  const idpName = useWatch({ control, name: 'idp.idp_name' }) as string | undefined;
  const isStrictMode = !!useWatch({ control, name: 'sso_settings.strict_sso_mode' });

  // Cognito returns the document it was given, so what the form holds until a new file is
  // dropped is the one already configured
  const metadataFileValue = useWatch({ control, name: 'idp.MetadataFile' });
  const hasExistingMetadataFile = !!metadataFileValue && !(metadataFileValue instanceof File);

  // derived URLs
  const callbackUrl = useMemo(
    () => `${COGNITO_BASE_URL}/${providerType === 'OIDC' ? 'oauth2' : 'saml2'}/idpresponse`,
    [providerType],
  );
  const signUpPortalUrl = useMemo(
    () => `${BASE_URL}/auth/sso/${organization?.id ?? ''}/${selectedProvider?.idp_name || idpName || ''}`,
    [organization?.id, selectedProvider?.idp_name, idpName],
  );

  return (
    <>
      {/* Strict SSO Alert */}
      {isStrictMode && (
        <StrictModeAlertBox>
          <Alert severity="warning">
            <strong>Strict SSO Mode is enabled.</strong> Authentication for this workspace and its members is limited to
            this Identity Provider only.
          </Alert>
        </StrictModeAlertBox>
      )}

      <FormGridContainer container spacing={2}>
        {/* Row: name + provider type */}
        <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
          <ControlledTextField
            control={control}
            fullWidth
            label="Identity provider name"
            name="idp.idp_name"
            disabled={selectedProvider !== null}
            variant="outlined"
            type="text"
            required
            rules={{
              ...REQUIRED_RULE,
              validate: (value?: string) =>
                !value || /^[a-zA-Z0-9-]+$/.test(value) || 'Must only include alphanumerical characters and hyphens',
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
          <Box sx={{ width: '100%' }}>
            <ControlledSelect
              variant="outlined"
              control={control}
              required
              fullWidth
              disabled={selectedProvider !== null}
              label="Provider type"
              name="idp.provider_type"
              options={[
                <MenuItem key="OIDC" value="OIDC">
                  OpenID Connect
                </MenuItem>,
                <MenuItem key="SAML" value="SAML">
                  SAML
                </MenuItem>,
              ]}
              rules={{ ...REQUIRED_RULE }}
            />
          </Box>
        </Grid>

        {/* OIDC Fields */}
        {providerType === 'OIDC' && (
          <>
            <Grid item xs={12}>
              <ControlledTextField
                control={control}
                fullWidth
                label="Client ID"
                name="idp.client_id"
                variant="outlined"
                type="text"
                required
                rules={{ ...REQUIRED_RULE }}
              />
            </Grid>
            <Grid item xs={12}>
              <ControlledTextField
                control={control}
                fullWidth
                label="Client secret"
                name="idp.client_secret"
                variant="outlined"
                type="text"
                required
                rules={{ ...REQUIRED_RULE }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ width: '100%' }}>
                <ControlledSelect
                  variant="outlined"
                  control={control}
                  fullWidth
                  label="Attributes request method"
                  name="idp.attributes_request_method"
                  options={[
                    <MenuItem key="GET" value="GET">
                      GET
                    </MenuItem>,
                    <MenuItem key="POST" value="POST">
                      POST
                    </MenuItem>,
                  ]}
                  required
                  rules={{ ...REQUIRED_RULE }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <ControlledTextField
                control={control}
                fullWidth
                label="OIDC issuer"
                name="idp.oidc_issuer"
                variant="outlined"
                type="text"
                required
                rules={{ ...REQUIRED_RULE, ...HTTPS_RULE }}
              />
            </Grid>
            <Grid item xs={12}>
              <ControlledTextField
                control={control}
                fullWidth
                label="Authorize scopes"
                variant="outlined"
                name="idp.authorize_scopes"
                type="text"
                required
                rules={{
                  ...REQUIRED_RULE,
                  validate: (value: string): string | boolean => value.includes('openid') || 'openid is required',
                }}
              />
              <RightHint variant="body2">
                Values for authorize scopes must be comma- or space-separated depending on your identity provider
              </RightHint>
            </Grid>
            <Grid item xs={12}>
              <ControlledTextField
                control={control}
                fullWidth
                label="Authorize URL"
                name="idp.authorize_url"
                variant="outlined"
                type="text"
                required
                rules={{ ...REQUIRED_RULE, ...HTTPS_RULE }}
              />
            </Grid>
            <Grid item xs={12}>
              <ControlledTextField
                control={control}
                fullWidth
                label="Token URL"
                name="idp.token_url"
                variant="outlined"
                type="text"
                required
                rules={{ ...REQUIRED_RULE, ...HTTPS_RULE }}
              />
            </Grid>
            <Grid item xs={12}>
              <ControlledTextField
                control={control}
                fullWidth
                label="Attributes URL"
                name="idp.attributes_url"
                variant="outlined"
                type="text"
                required
                rules={{ ...REQUIRED_RULE, ...HTTPS_RULE }}
              />
            </Grid>
            <Grid item xs={12}>
              <ControlledTextField
                control={control}
                fullWidth
                label="JWKS URI"
                name="idp.jwks_uri"
                variant="outlined"
                type="text"
                required
                rules={{ ...REQUIRED_RULE, ...HTTPS_RULE }}
              />
            </Grid>
          </>
        )}

        {/* SAML Fields */}
        {providerType === 'SAML' && (
          <>
            <Grid item xs={12}>
              <Typography>
                Metadata file<sup>*</sup>
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
                  <Select
                    label="Metadata Type"
                    fullWidth
                    variant="outlined"
                    onChange={(event) => onMetadataTypeChange(event.target.value as MetadataType)}
                    value={metadataType}
                  >
                    <MenuItem key="URL" value="MetadataURL">
                      URL
                    </MenuItem>
                    <MenuItem key="FILE" value="MetadataFile">
                      File
                    </MenuItem>
                  </Select>
                </Grid>
                <Grid item xs={12} md={8} sx={{ minWidth: 0 }}>
                  {metadataType === 'MetadataFile' ? (
                    <>
                      <ControlledDropzone
                        control={control}
                        fullWidth
                        name="idp.MetadataFile"
                        rules={{ ...REQUIRED_RULE }}
                      />
                      {hasExistingMetadataFile && (
                        <Typography variant="caption" component="div">
                          A metadata file is already configured for this identity provider. Uploading a new file will
                          replace it.
                        </Typography>
                      )}
                    </>
                  ) : (
                    <ControlledTextField
                      control={control}
                      fullWidth
                      label="Metadata File URL"
                      name="idp.MetadataURL"
                      variant="outlined"
                      type="text"
                      required
                      rules={{ ...REQUIRED_RULE }}
                    />
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} mt={0}>
              <ControlledCheckbox
                control={control}
                label="Enable IdP sign out flow"
                name="idp.IDPSignout"
                color="primary"
              />
            </Grid>
          </>
        )}

        {/* Strict SSO Mode */}
        <Grid item xs={12} sx={{ mt: 0 }}>
          <ControlledCheckbox
            control={control}
            label="Enable Strict SSO Mode"
            name="sso_settings.strict_sso_mode"
            color="primary"
          />
          <Typography variant="caption" component="div">
            If enabled, it will only be possible to authenticate or join the workspace using this identity provider. All
            other means of authentication will be disabled.
          </Typography>
        </Grid>

        {/* Attribute Mapping */}
        <SectionItemGrid item xs={12}>
          <Typography variant="subtitle1">Attribute Mapping</Typography>
          <Typography variant="subtitle2">
            In order to let users log in from your organization you need to map user attributes from your identity
            provider to the corresponding attributes below
          </Typography>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" align="center">
                Your attributes
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" align="center">
                Our attributes
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
              <ControlledTextField
                control={control}
                fullWidth
                required
                label="Email attribute"
                name="idp.attribute_mapping.email"
                variant="outlined"
                type="text"
                rules={{ ...REQUIRED_RULE }}
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
              <TextField variant="outlined" disabled fullWidth label="Email" />
            </Grid>

            <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
              <ControlledTextField
                control={control}
                fullWidth
                label="First name attribute"
                name="idp.attribute_mapping.given_name"
                variant="outlined"
                type="text"
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
              <TextField variant="outlined" disabled fullWidth label="First name" />
            </Grid>

            <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
              <ControlledTextField
                control={control}
                fullWidth
                label="Last name attribute"
                name="idp.attribute_mapping.family_name"
                variant="outlined"
                type="text"
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
              <TextField variant="outlined" disabled fullWidth label="Last name" />
            </Grid>
          </Grid>
        </SectionItemGrid>

        {/* Callback URL */}
        <SectionItemGrid item xs={12}>
          <Typography variant="subtitle1">Callback URL</Typography>
          <Typography variant="subtitle2" paragraph>
            In order to configure your identity providers you may need the following callback URL
          </Typography>
          <CodeVisualizer commands={[callbackUrl]} singleLineMode />
        </SectionItemGrid>

        {/* Identifier */}
        <SectionItemGrid item xs={12}>
          <Typography variant="subtitle1">Identifier (Entity Id)</Typography>
          <Typography variant="subtitle2" paragraph>
            In order to configure your identity providers you may need the following Cognito identifier
          </Typography>
          <CodeVisualizer commands={[COGNITO_IDENTIFIER]} />
        </SectionItemGrid>

        {/* Sign Up Portal */}
        <SectionItemGrid item xs={12}>
          <Typography variant="subtitle1">Sign Up Portal</Typography>
          <Typography variant="subtitle2" paragraph>
            Users from your organization can sign up through this identity provider via below link
          </Typography>
          <CodeVisualizer data-testid={TEST_IDS.SSO_SIGN_UP_PORTAL} commands={[signUpPortalUrl]} />
          <Typography variant="caption">
            After signing up, users can also sign in normally on{' '}
            <InlineLink target="_blank" rel="noreferrer" href={ExternalLink.WEB_APP_SIGN_IN}>
              app.localstack.cloud
            </InlineLink>
          </Typography>
        </SectionItemGrid>

        {/* Sign Up Settings */}
        <SectionItemGrid item xs={12}>
          <Typography variant="subtitle1">Sign Up Settings</Typography>
          <Box mt={2}>
            <Box sx={{ width: '100%' }}>
              <ControlledSelect
                variant="outlined"
                control={control}
                fullWidth
                label="Default User Role"
                name="sso_settings.default_membership_role"
                options={[
                  <MenuItem key={UserRole.MEMBER} value={UserRole.MEMBER}>
                    Member
                  </MenuItem>,
                  <MenuItem key={UserRole.ADMIN} value={UserRole.ADMIN}>
                    Admin
                  </MenuItem>,
                ]}
              />
            </Box>
            <Typography variant="caption">
              If specified, all users from this identity provider will be assigned selected role upon sign up, default
              is &apos;MEMBER&apos; role
            </Typography>
          </Box>

          <Box mt={2}>
            <Box sx={{ width: '100%' }}>
              <ControlledSelect
                variant="outlined"
                control={control}
                fullWidth
                multiple
                label="Default User Permissions"
                name="sso_settings.default_membership_permissions"
                options={Object.entries(PERMISSION_GROUPS).reduce(
                  (memo, [group, permissions]) => [
                    ...memo,
                    <MenuItem disabled key={group} value={group}>
                      {group}
                    </MenuItem>,
                    ...permissions.map((permission) => (
                      <MenuItem key={permission} value={permission}>
                        {PERMISSION_TYPES[permission as keyof typeof PERMISSION_TYPES]}
                      </MenuItem>
                    )),
                  ],
                  [] as JSX.Element[],
                )}
              />
            </Box>
            <Typography variant="caption">
              If specified, all users from this identity provider will be <strong>additionally</strong> assigned
              selected permissions upon sign up
            </Typography>
          </Box>

          <Box mt={2}>
            <ControlledCheckbox
              control={control}
              name="sso_settings.assign_license_on_join"
              label="Automatically assign a license when the user joins (subject to availability)"
            />
          </Box>
        </SectionItemGrid>
      </FormGridContainer>
    </>
  );
};
